import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Folder, Plus, Loader2, ArrowRight, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { devError } from '../utils/logger';

/**
 * /projects — grid of every project the signed-in user owns. Click a tile
 * to open the project detail page; click "+ New project" to spin up a new
 * one inline.
 */
export default function ProjectsPage() {
  const [projects, setProjects] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/projects`);
      setProjects(data.projects || []);
    } catch (e) {
      devError('projects load failed', e);
      setProjects([]);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/projects`, { name: name.trim(), description: '' });
      toast.success('Project created');
      setName('');
      setCreating(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="projects-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between gap-3 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2 inline-flex items-center gap-1.5">
              <Folder className="w-3 h-3" /> Your sourcing
            </p>
            <h1 className="font-playfair text-4xl md:text-5xl">Projects</h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Save listings into named projects so each job has its own moodboard. Add directly from any listing page using "Save to project".
            </p>
          </div>
          {!creating && (
            <Button className="btn-gold" onClick={() => setCreating(true)} data-testid="new-project-trigger">
              <Plus className="w-4 h-4 mr-2" /> New project
            </Button>
          )}
        </div>

        {creating && (
          <form onSubmit={create} className="bg-card border border-border/40 rounded-xl p-5 mb-8 flex flex-wrap gap-3 items-center" data-testid="new-project-form">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Coastal house — kitchen"
              maxLength={80}
              className="flex-1 min-w-[240px] px-3 py-2 bg-secondary border border-border/40 rounded-md focus:outline-none focus:border-accent"
              data-testid="new-project-input"
            />
            <Button type="submit" disabled={saving} className="btn-gold" data-testid="new-project-create">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
            <button type="button" onClick={() => { setCreating(false); setName(''); }} className="text-sm text-muted-foreground">Cancel</button>
          </form>
        )}

        {projects === null && (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading projects…
          </div>
        )}

        {projects && projects.length === 0 && !creating && (
          <div className="bg-card border border-border/30 rounded-xl p-12 text-center" data-testid="projects-empty">
            <Folder className="w-10 h-10 text-accent/50 mx-auto mb-3" />
            <h2 className="font-playfair text-xl mb-2">Start your first project</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Projects are how you organise reclaimed materials into named buckets — one per job, one per room, one per mood. Browse the marketplace and tap "Save to project" on anything you fancy.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <Button className="btn-gold" onClick={() => setCreating(true)}>
                <Plus className="w-4 h-4 mr-2" /> New project
              </Button>
              <Link to="/marketplace">
                <Button variant="outline" className="border-border/40">Browse marketplace <ArrowRight className="w-3 h-3 ml-2" /></Button>
              </Link>
            </div>
          </div>
        )}

        {projects && projects.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p) => (
              <Link
                key={p.project_id}
                to={`/projects/${p.project_id}`}
                className="group rounded-xl overflow-hidden border border-border/30 bg-card hover:border-accent/40 transition-colors"
                data-testid={`project-card-${p.project_id}`}
              >
                <div
                  className="aspect-[16/10] bg-secondary/40 bg-cover bg-center flex items-end relative"
                  style={p.cover_url ? { backgroundImage: `url(${p.cover_url})` } : {}}
                >
                  {!p.cover_url && (
                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
                      <ImageOff className="w-8 h-8" />
                    </div>
                  )}
                  <div className="relative w-full p-3 bg-gradient-to-t from-black/70 to-transparent">
                    <p className="text-xs uppercase tracking-widest text-white/70">{(p.listing_ids || []).length} item{(p.listing_ids || []).length === 1 ? '' : 's'}</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-playfair text-lg group-hover:text-accent transition-colors line-clamp-1">{p.name}</p>
                  {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
